// Edit sheet for item_holster_attributes (P1-18 PR B).

import {
  HOLSTER_DRAW_SIDES,
  HOLSTER_MATERIALS,
  HOLSTER_MOUNT_TYPES,
  validateHolsterAttributes,
} from "@betterpos/domain";
import { useState } from "react";
import { readSessionToken } from "../../data/audit.ts";
import type { HolsterAttributes } from "../../data/catalog-attrs.ts";
import { type HolsterAttributesUpdate, updateItemHolster } from "../../data/catalog-edits.ts";
import { AttrSheet, messageForEditError } from "./AttrSheet.tsx";
import { Field, Segmented, TextInput } from "./SheetControls.tsx";

type Props = {
  itemId: string;
  current: HolsterAttributes;
  onSaved: (next: HolsterAttributesUpdate) => void;
  onCancel: () => void;
};

const MOUNT_OPTIONS = HOLSTER_MOUNT_TYPES.map((v) => ({
  value: v,
  label:
    v === "iwb" || v === "owb" ? v.toUpperCase() : v === "drop_leg" ? "Drop-leg" : titleCase(v),
}));
const DRAW_OPTIONS = HOLSTER_DRAW_SIDES.map((v) => ({ value: v, label: titleCase(v) }));
const MATERIAL_OPTIONS = HOLSTER_MATERIALS.map((v) => ({ value: v, label: titleCase(v) }));

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function HolsterAttrsSheet({ itemId, current, onSaved, onCancel }: Props) {
  const [manufacturer, setManufacturer] = useState(current.manufacturer);
  const [fitModel, setFitModel] = useState(current.fitModel);
  const [mountType, setMountType] = useState<HolsterAttributes["mountType"]>(current.mountType);
  const [drawSide, setDrawSide] = useState<HolsterAttributes["drawSide"]>(current.drawSide);
  const [retentionLevel, setRetentionLevel] = useState(current.retentionLevel ?? "");
  const [material, setMaterial] = useState<HolsterAttributes["material"]>(current.material);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const candidate = {
    manufacturer,
    fitModel,
    mountType,
    drawSide,
    retentionLevel: retentionLevel === "" ? null : retentionLevel,
    material,
  };
  const validation = validateHolsterAttributes(candidate);
  const changed =
    manufacturer.trim() !== current.manufacturer ||
    fitModel.trim() !== current.fitModel ||
    mountType !== current.mountType ||
    drawSide !== current.drawSide ||
    (retentionLevel.trim() || null) !== current.retentionLevel ||
    material !== current.material;
  const canSave = validation.ok && changed && !saving;

  const save = async () => {
    if (!validation.ok) return;
    const token = readSessionToken();
    if (!token) {
      setError("Not signed in.");
      return;
    }
    setSaving(true);
    setError(null);
    const res = await updateItemHolster(token, itemId, {
      expectedVersion: current.version,
      attributes: validation.value,
    });
    setSaving(false);
    if (!res.ok) {
      setError(messageForEditError(res, "This field"));
      return;
    }
    onSaved(res.value);
  };

  // Guard the type narrowing on Segmented's onChange — mountType and
  // drawSide are non-nullable at the DB level, so pass through only
  // non-null picks.
  const onMountChange = (next: HolsterAttributes["mountType"] | null) => {
    if (next !== null) setMountType(next);
  };
  const onDrawChange = (next: HolsterAttributes["drawSide"] | null) => {
    if (next !== null) setDrawSide(next);
  };

  return (
    <AttrSheet
      title="Edit holster attributes"
      error={error}
      saving={saving}
      canSave={canSave}
      onCancel={onCancel}
      onSave={save}
    >
      <Field label="Manufacturer">
        <TextInput value={manufacturer} onChange={setManufacturer} disabled={saving} />
      </Field>
      <Field label="Fits">
        <TextInput
          value={fitModel}
          onChange={setFitModel}
          disabled={saving}
          placeholder="e.g. Glock 19 / Sig P365"
        />
      </Field>
      <Field label="Mount type">
        <Segmented
          value={mountType}
          options={MOUNT_OPTIONS}
          onChange={onMountChange}
          disabled={saving}
        />
      </Field>
      <Field label="Draw side">
        <Segmented
          value={drawSide}
          options={DRAW_OPTIONS}
          onChange={onDrawChange}
          disabled={saving}
        />
      </Field>
      <Field label="Retention">
        <TextInput
          value={retentionLevel}
          onChange={setRetentionLevel}
          disabled={saving}
          placeholder="Level 1 / Level 2 / Passive"
        />
      </Field>
      <Field label="Material">
        <Segmented
          value={material}
          options={MATERIAL_OPTIONS}
          onChange={setMaterial}
          disabled={saving}
          nullable
        />
      </Field>
    </AttrSheet>
  );
}
